import { useState } from "react";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogFooter,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import {
  collaboratorService,
  CreateCollaboratorRequest,
  ECollaboratorRole,
} from "@/services/collaborator";

interface CreateCollaboratorModalProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

const initialFormData: CreateCollaboratorRequest = {
  email: "",
  password: "",
  firstName: "",
  lastName: "",
  role: ECollaboratorRole.SELLER,
};

export function CreateCollaboratorModal({
  open,
  onOpenChange,
}: CreateCollaboratorModalProps) {
  const queryClient = useQueryClient();

  const [formData, setFormData] =
    useState<CreateCollaboratorRequest>(initialFormData);

  const handleClose = () => {
    setFormData(initialFormData);
    onOpenChange(false);
  };

  const createMutation = useMutation({
    mutationFn: (data: CreateCollaboratorRequest) =>
      collaboratorService.create(data),
    onSuccess: () => {
      toast.success("Colaborador registrado exitosamente");
      queryClient.invalidateQueries({ queryKey: ["collaborators"] });
      handleClose();
    },
    onError: (error: Error) => {
      toast.error(error.message);
    },
  });

  const handleSubmit = () => {
    if (
      !formData.email.trim() ||
      !formData.password.trim() ||
      !formData.firstName.trim() ||
      !formData.lastName.trim()
    ) {
      toast.error("Email, contraseña, nombre y apellido son requeridos");
      return;
    }
    createMutation.mutate(formData);
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && handleClose()}>
      <DialogContent className="sm:max-w-md">
        <DialogHeader>
          <DialogTitle>Registrar colaborador</DialogTitle>
          <DialogDescription>
            Ingresa los datos del nuevo colaborador.
          </DialogDescription>
        </DialogHeader>

        <div className="space-y-4">
          <div className="space-y-2">
            <Label htmlFor="email">
              Email <span className="text-destructive">*</span>
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="colaborador@example.com"
              value={formData.email}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, email: e.target.value }))
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="password">
              Contraseña <span className="text-destructive">*</span>
            </Label>
            <Input
              id="password"
              type="password"
              placeholder="••••••••"
              value={formData.password}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, password: e.target.value }))
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="firstName">
              Nombre <span className="text-destructive">*</span>
            </Label>
            <Input
              id="firstName"
              placeholder="Juan"
              value={formData.firstName}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, firstName: e.target.value }))
              }
            />
          </div>

          <div className="space-y-2">
            <Label htmlFor="lastName">
              Apellido <span className="text-destructive">*</span>
            </Label>
            <Input
              id="lastName"
              placeholder="Pérez"
              value={formData.lastName}
              onChange={(e) =>
                setFormData((prev) => ({ ...prev, lastName: e.target.value }))
              }
            />
          </div>

          <div className="space-y-2">
            <Label>Rol</Label>
            <Select
              value={formData.role}
              onValueChange={(val) =>
                setFormData((prev) => ({
                  ...prev,
                  role: val as ECollaboratorRole,
                }))
              }
            >
              <SelectTrigger>
                <SelectValue placeholder="Seleccionar rol" />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value={ECollaboratorRole.OWNER}>
                  Propietario
                </SelectItem>
                <SelectItem value={ECollaboratorRole.SELLER}>
                  Vendedor
                </SelectItem>
              </SelectContent>
            </Select>
          </div>
        </div>

        <DialogFooter>
          <Button
            variant="outline"
            onClick={handleClose}
            disabled={createMutation.isPending}
          >
            Cancelar
          </Button>
          <Button onClick={handleSubmit} disabled={createMutation.isPending}>
            {createMutation.isPending ? (
              <Loader2 className="h-4 w-4 animate-spin mr-2" />
            ) : null}
            Registrar
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
